import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useLocation, useSearch } from "wouter";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Trophy, Loader2 } from "lucide-react";
import type { Bot, Scenario } from "@shared/schema";

interface DuelEvent {
  type: string;
  round?: number;
  message?: string;
  botName?: string;
  botAvatar?: string;
  scoreA?: number;
  scoreB?: number;
  pointsA?: number;
  pointsB?: number;
  winner?: string;
  analysis?: string;
  duelId?: string;
  leg?: number;
  judgeName?: string;
  judgeAvatar?: string;
  verdict?: { judgeId: string; judgeName: string; votedFor: string; comment: string };
  verdicts?: { judgeId: string; judgeName: string; votedFor: string; comment: string }[];
  trainerComment?: { trainerId: string; trainerName: string; trainerAvatar: string; feedbackA: string; feedbackB: string };
  trainerComments?: { trainerId: string; trainerName: string; trainerAvatar: string; feedbackA: string; feedbackB: string }[];
  isClassic?: boolean;
}

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 px-3 py-2">
      <div className="w-2 h-2 rounded-full bg-muted-foreground/50 typing-dot" />
      <div className="w-2 h-2 rounded-full bg-muted-foreground/50 typing-dot" />
      <div className="w-2 h-2 rounded-full bg-muted-foreground/50 typing-dot" />
    </div>
  );
}

export default function DuelPage() {
  const [, navigate] = useLocation();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const scenarioId = params.get("scenario") || "";
  const botAId = params.get("botA") || "";
  const botBId = params.get("botB") || "";
  const judgeIdsParam = params.get("judges") || "";
  const judgeIds = judgeIdsParam ? judgeIdsParam.split(",").filter(Boolean) : [];
  const duelMode = params.get("mode") || "express";

  const [events, setEvents] = useState<DuelEvent[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isJudging, setIsJudging] = useState(false);
  const [duelError, setDuelError] = useState<string | null>(null);
  const [currentRound, setCurrentRound] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: bots = [] } = useQuery<Bot[]>({ queryKey: ["/api/bots"] });
  const { data: scenarios = [] } = useQuery<Scenario[]>({ queryKey: ["/api/scenarios"] });
  const { data: extBots = [] } = useQuery<{ id: string; name: string; avatar: string }[]>({
    queryKey: ["/api/external-bots/all"],
  });

  const botA = useMemo(() => {
    const b = bots.find(x => x.id === botAId);
    if (b) return b;
    if (botAId.startsWith("ext:")) {
      const e = extBots.find(x => `ext:${x.id}` === botAId);
      if (e) return { id: botAId, name: e.name, avatar: e.avatar, style: "", description: "", strategy: "" } as Bot;
    }
    return undefined;
  }, [bots, extBots, botAId]);

  const botB = useMemo(() => {
    const b = bots.find(x => x.id === botBId);
    if (b) return b;
    if (botBId.startsWith("ext:")) {
      const e = extBots.find(x => `ext:${x.id}` === botBId);
      if (e) return { id: botBId, name: e.name, avatar: e.avatar, style: "", description: "", strategy: "" } as Bot;
    }
    return undefined;
  }, [bots, extBots, botBId]);
  const scenario = scenarios.find(s => s.id === scenarioId);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [events]);

  useEffect(() => {
    if (!scenarioId || !botAId || !botBId || isRunning) return;

    setIsRunning(true);
    setDuelError(null);

    const API_BASE = "__PORT_5000__".startsWith("__") ? "" : "__PORT_5000__";
    const controller = new AbortController();

    fetch(`${API_BASE}/api/duels/bot-vs-bot`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ scenarioId, botAId, botBId, judgeIds, mode: duelMode }),
      signal: controller.signal,
    }).then(async (response) => {
      if (!response.ok) {
        const text = await response.text();
        setDuelError(text || `Ошибка запуска поединка (HTTP ${response.status})`);
        setIsRunning(false);
        setIsJudging(false);
        return;
      }
      const reader = response.body?.getReader();
      if (!reader) {
        setDuelError("Поток поединка недоступен");
        setIsRunning(false);
        setIsJudging(false);
        return;
      }
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          setIsRunning(false);
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";

        for (const chunk of chunks) {
          const dataLines = chunk
            .split("\n")
            .map((line) => line.trim())
            .filter((line) => line.startsWith("data:"))
            .map((line) => line.slice(5).trim());
          if (dataLines.length > 0) {
            const data = dataLines.join("\n");
            if (data === "[DONE]") {
              setIsRunning(false);
              return;
            }
            try {
              const event: DuelEvent = JSON.parse(data);
              if (event.type === "round_start") {
                setCurrentRound(event.round || 0);
              }
              if (event.type === "judging") {
                setIsJudging(true);
              }
              if (event.type === "error") {
                setDuelError(event.message || "Ошибка запуска поединка");
                setIsRunning(false);
                setIsJudging(false);
              }
              if (event.type === "duel_complete") {
                setIsComplete(true);
                setIsJudging(false);
                // Invalidate duels cache so History tab shows updated data
                queryClient.invalidateQueries({ queryKey: ["/api/duels"] });
                queryClient.invalidateQueries({ queryKey: ["/api/bots"] });
                queryClient.invalidateQueries({ queryKey: ["/api/external-bots/all"] });
              }
              setEvents(prev => [...prev, event]);
            } catch {}
          }
        }
      }
    }).catch(() => {
      setDuelError("Не удалось получить поток событий поединка");
      setIsRunning(false);
      setIsJudging(false);
    });

    return () => {
      controller.abort();
    };
  }, [scenarioId, botAId, botBId, duelMode, judgeIdsParam]);

  const messages = events.filter(e =>
    e.type === "bot_a_message" || e.type === "bot_b_message" ||
    e.type === "leg2_start" || e.type === "judge_evaluating" ||
    e.type === "judge_verdict" || e.type === "trainer_evaluating" ||
    e.type === "trainer_comment"
  );
  const result = events.find(e => e.type === "duel_complete");
  const maxRounds = scenario?.maxRounds || 8;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Button variant="ghost" size="sm" className="mb-4 gap-1" onClick={() => navigate("/")} data-testid="button-back">
        <ArrowLeft className="w-4 h-4" /> Назад к арене
      </Button>

      {/* Header */}
      {duelError && (
        <Card className="p-4 mb-4 border-destructive/40 bg-destructive/10" data-testid="duel-error-banner">
          <div className="text-sm text-destructive font-medium">Ошибка поединка</div>
          <p className="text-sm text-destructive/90 mt-1">{duelError}</p>
        </Card>
      )}
      {scenario && (
        <Card className="p-5 mb-6">
          <h1 className="text-lg font-semibold mb-1" data-testid="text-duel-title">{scenario.title}</h1>
          <p className="text-sm text-muted-foreground">{scenario.description}</p>
          <div className="flex items-center gap-4 mt-3">
            <Progress value={(currentRound / maxRounds) * 100} className="flex-1" />
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              Раунд {currentRound}/{maxRounds}
            </span>
          </div>
        </Card>
      )}

      {/* VS Header */}
      {botA && botB && (
        <div className="flex items-center justify-center gap-6 mb-6">
          <div className="text-center">
            <span className="text-3xl block mb-1">{botA.avatar}</span>
            <span className="text-sm font-medium text-red-400">{botA.name}</span>
          </div>
          <span className="text-lg font-bold text-muted-foreground">VS</span>
          <div className="text-center">
            <span className="text-3xl block mb-1">{botB.avatar}</span>
            <span className="text-sm font-medium text-blue-400">{botB.name}</span>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="space-y-3 mb-6" data-testid="duel-messages">
        {messages.map((msg, i) => {
          const isA = msg.type === "bot_a_message";
          const isMsg = msg.type === "bot_a_message" || msg.type === "bot_b_message";
          const isLeg2Start = msg.type === "leg2_start";
          const isJudgeEval = msg.type === "judge_evaluating";
          const isJudgeVerdict = msg.type === "judge_verdict";

          if (isLeg2Start) {
            return (
              <div key={i} className="text-center py-3">
                <Badge variant="outline" className="text-xs">Смена сторон — второй раунд</Badge>
              </div>
            );
          }
          if (isJudgeEval) {
            return (
              <div key={i} className="flex items-center justify-center gap-2 text-xs text-muted-foreground py-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                {msg.judgeName} оценивает...
              </div>
            );
          }
          if (isJudgeVerdict && msg.verdict) {
            const v = msg.verdict;
            return (
              <div key={i} className="flex justify-center">
                <Card className="w-full max-w-lg p-3 bg-amber-500/5 border-amber-500/20 space-y-1">
                  <div className="flex items-center gap-2 text-xs font-medium">
                    <span>⚖️ {v.judgeName}</span>
                    <span className="text-muted-foreground">→</span>
                    <Badge variant="outline" className={`text-xs ${v.votedFor === botAId ? "text-red-400 border-red-500/20" : "text-blue-400 border-blue-500/20"}`}>
                      {v.votedFor === botAId ? botA?.name : botB?.name} (+1 очко)
                    </Badge>
                  </div>
                  {v.comment && <p className="text-xs text-muted-foreground">{v.comment}</p>}
                </Card>
              </div>
            );
          }

          // Trainer events
          if (msg.type === "trainer_evaluating") {
            return (
              <div key={i} className="flex items-center justify-center gap-2 text-xs text-muted-foreground py-2">
                <Loader2 className="w-3 h-3 animate-spin" />
                Тренеры готовят комментарии...
              </div>
            );
          }
          if (msg.type === "trainer_comment" && msg.trainerComment) {
            const tc = msg.trainerComment;
            return (
              <div key={i} className="flex justify-center">
                <Card className="w-full max-w-lg p-3 bg-green-500/5 border-green-500/20 space-y-2">
                  <div className="text-xs font-medium flex items-center gap-1">
                    🎓 {tc.trainerName} — тренерский комментарий
                  </div>
                  {tc.feedbackA && (
                    <div className="text-xs">
                      <span className="font-medium text-red-400">{botA?.name}:</span>
                      <span className="text-muted-foreground ml-1">{tc.feedbackA}</span>
                    </div>
                  )}
                  {tc.feedbackB && (
                    <div className="text-xs">
                      <span className="font-medium text-blue-400">{botB?.name}:</span>
                      <span className="text-muted-foreground ml-1">{tc.feedbackB}</span>
                    </div>
                  )}
                </Card>
              </div>
            );
          }

          if (!isMsg) return null;

          return (
            <div key={i} className={`flex ${isA ? "justify-start" : "sm:justify-end"}`}>
              <div className={`w-full sm:max-w-[80%] ${isA ? "sm:mr-auto" : "sm:ml-auto"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm">{msg.botAvatar}</span>
                  <span className={`text-xs font-medium ${isA ? "text-red-400" : "text-blue-400"}`}>
                    {msg.botName}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Раунд {msg.round}{msg.leg === 2 ? " (смена сторон)" : ""}
                  </span>
                </div>
                <div
                  className={`rounded-lg px-4 py-3 text-sm ${
                    isA ? "bg-red-500/10 border border-red-500/20" : "bg-blue-500/10 border border-blue-500/20"
                  }`}
                  data-testid={`message-${isA ? "a" : "b"}-${msg.round}`}
                >
                  {msg.message}
                </div>
              </div>
            </div>
          );
        })}

        {isRunning && !isJudging && !isComplete && !duelError && <TypingIndicator />}

        {isJudging && (
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="w-4 h-4 animate-spin" />
            Судьи оценивают поединок...
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Result */}
      {result && (
        <Card className="p-6 space-y-4 glow-amber" data-testid="duel-result">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">Результат поединка</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            <div>
              <span className="text-2xl block mb-1">{botA?.avatar}</span>
              <span className="text-sm font-medium text-red-400">{botA?.name}</span>
              <div className="text-2xl font-bold mt-1">{result.pointsA ?? result.scoreA}</div>
              <span className="text-xs text-muted-foreground">{result.pointsA !== undefined ? "очков" : "баллов"}</span>
            </div>
            <div className="flex items-center justify-center">
              <Badge className="text-lg px-4 py-1" variant={result.winner === "draw" ? "secondary" : "default"}>
                {result.winner === "draw"
                  ? "Ничья"
                  : result.winner === botAId
                    ? `${botA?.avatar} Победа`
                    : `${botB?.avatar} Победа`}
              </Badge>
            </div>
            <div>
              <span className="text-2xl block mb-1">{botB?.avatar}</span>
              <span className="text-sm font-medium text-blue-400">{botB?.name}</span>
              <div className="text-2xl font-bold mt-1">{result.pointsB ?? result.scoreB}</div>
              <span className="text-xs text-muted-foreground">{result.pointsB !== undefined ? "очков" : "баллов"}</span>
            </div>
          </div>

          {result.isClassic && (
            <div className="text-center text-xs text-muted-foreground bg-muted/20 rounded p-2">
              Классический поединок со сменой сторон — очки присвоены по итогам обоих раундов
            </div>
          )}

          {/* Judge verdicts — vertical layout */}
          {result.verdicts && result.verdicts.length > 0 && (
            <div className="bg-muted/30 rounded-md p-4 mt-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">⚖️ Вердикты судей</h3>
              <div className="flex flex-col gap-3">
                {result.verdicts.map((v: any, vi: number) => (
                  <div key={vi} className="p-3 rounded-lg bg-background/50 border border-border/30 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">⚖️ {v.judgeName}</span>
                      <span className="text-muted-foreground">→</span>
                      <Badge variant="outline" className={`text-xs ${v.votedFor === botAId ? "text-red-400 border-red-500/20" : "text-blue-400 border-blue-500/20"}`}>
                        {v.votedFor === botAId ? botA?.name : botB?.name} (+1 очко)
                      </Badge>
                    </div>
                    {v.comment && <p className="text-xs text-muted-foreground leading-relaxed">{v.comment}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.analysis && !result.verdicts?.length && (
            <div className="bg-muted/30 rounded-md p-4 mt-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Анализ судьи</h3>
              <p className="text-sm">{result.analysis}</p>
            </div>
          )}

          {/* Trainer comments */}
          {result.trainerComments && result.trainerComments.length > 0 && (
            <div className="bg-green-500/5 rounded-md p-4 mt-2 border border-green-500/10">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-green-400/80 mb-3">🎓 Тренерские комментарии</h3>
              <div className="flex flex-col gap-3">
                {result.trainerComments.map((tc: any, ti: number) => (
                  <div key={ti} className="p-3 rounded-lg bg-background/50 border border-green-500/10 space-y-2">
                    <div className="text-xs font-medium">🎓 {tc.trainerName}</div>
                    {tc.feedbackA && (
                      <div className="text-xs">
                        <span className="font-medium text-red-400">{botA?.name}:</span>
                        <span className="text-muted-foreground ml-1 leading-relaxed">{tc.feedbackA}</span>
                      </div>
                    )}
                    {tc.feedbackB && (
                      <div className="text-xs">
                        <span className="font-medium text-blue-400">{botB?.name}:</span>
                        <span className="text-muted-foreground ml-1 leading-relaxed">{tc.feedbackB}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button
            className="w-full mt-4"
            onClick={() => navigate("/")}
            data-testid="button-new-duel"
          >
            Новый поединок
          </Button>
        </Card>
      )}
    </div>
  );
}
